/**
 * Monetization / funnel event logger — FIRST-PARTY ONLY.
 *
 * No third-party analytics. Events are sent only to an optional self-hosted
 * collector (REACT_APP_TELEMETRY_ENDPOINT, your own server). When that is unset
 * — which is the default — this module is a no-op. There is no PostHog, no Google
 * Analytics, and no data leaves to any external analytics provider.
 *
 * Anonymous product usage (installs, feature usage, retention) is handled
 * separately by src/services/analytics.ts, which writes our own backend's
 * anonymous, aggregate tables.
 */

// ── Event catalogue ───────────────────────────────────────────────────────────

export type MonetizationEventName =
  | 'pro_lock_seen'
  | 'upgrade_clicked'
  | 'manage_subscription_clicked'
  | 'restore_clicked'
  | 'redeem_clicked'
  | 'checkout_started'
  | 'checkout_success'
  // Web upgrade page (account.rhemn.com/upgrade) sign-in step.
  | 'upgrade_signin_shown'
  | 'upgrade_signin_completed'
  // iOS native StoreKit purchase that terminated without a sheet/receipt.
  // Always carries a `reason` property (see nativeIAP.PurchaseFailureReason)
  // so a TestFlight report is diagnosable from telemetry alone.
  | 'purchase_failed';

export type FeatureEventName =
  | 'todo_added'
  | 'todo_completed'
  | 'note_created'
  | 'note_analyzed'
  | 'qibla_opened'
  | 'prayer_times_viewed'
  | 'verse_favorited';

export type AnalyticsEventName = MonetizationEventName | FeatureEventName;

// ── Transport (optional, self-hosted) ──────────────────────────────────────────

const ENDPOINT = process.env.REACT_APP_TELEMETRY_ENDPOINT;
const DEBUG = process.env.REACT_APP_TELEMETRY_DEBUG === 'true';

export async function trackEvent(
  event: AnalyticsEventName,
  properties: Record<string, unknown> = {},
): Promise<void> {
  try {
    if (ENDPOINT) {
      await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, properties, timestamp: new Date().toISOString() }),
      });
    }

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[telemetry]', event, properties);
    }
  } catch {
    // never break user UX for analytics
  }
}

// Backwards-compatible wrapper used by existing call sites
export const trackMonetizationEvent = (
  event: MonetizationEventName,
  source: string,
  metadata?: Record<string, unknown>,
): Promise<void> => trackEvent(event, { source, ...metadata });

const sentLockEvents = new Set<string>();

export const trackLockSeenOnce = async (source: string): Promise<void> => {
  const key = `lock:${source}`;
  if (sentLockEvents.has(key)) return;
  sentLockEvents.add(key);
  await trackMonetizationEvent('pro_lock_seen', source);
};
